import { useCallback } from 'react';

/**
 * Custom hook for tracking events with Plausible Analytics
 *
 * @returns {Function} trackEvent - Function to track custom events with Plausible
 * @returns {string} trackEvent.eventName - The name of the event to track
 * @returns {Object} [trackEvent.props={}] - Optional event properties to send with the event
 * @returns {number} [trackEvent.props.revenue] - Optional revenue amount in EUR to track with the event (part of props)
 *
 * @example
 * const trackEvent = usePlausibleTracking();
 * trackEvent('Purchase', { product: 'Widget', revenue: 29.99 });
 */
const usePlausibleTracking = () => {
  /**
   * Tracks a custom event with Plausible Analytics
   *
   * @param {string} eventName - The name of the event to track
   * @param {Object} [props={}] - Custom properties to associate with the event
   * @param {number} [props.revenue] - Revenue amount in EUR to track with the event
   */
  const trackEvent = useCallback((eventName, props = {}) => {
    const { revenue, ...eventProps } = props;

    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible(eventName, {
        props: eventProps,
        ...(revenue ? { revenue: { currency: 'EUR', amount: revenue } } : {}),
      });
    }
  }, []);

  return trackEvent;
};

export default usePlausibleTracking;
